import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Mail, Loader2 } from "lucide-react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTENT_COPY = {
    book_demo: {
        title: "Book a demo",
        desc: "We'll reach out within 1 business day to walk you through TruoPay live.",
    },
    login: {
        title: "Early access",
        desc: "Drop your details and we'll get you in first.",
    },
    sign_up: {
        title: "Join the waitlist",
        desc: "Leave your details and we'll prioritise your invite.",
    },
    contact: {
        title: "Get in touch",
        desc: "Tell us about your business and we'll be in touch.",
    },
};

const validEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

export default function LeadModal({ open, onOpenChange, intent = "contact", label }) {
    const copy = INTENT_COPY[intent] || INTENT_COPY.contact;
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (open) {
            setName("");
            setEmail("");
            setError("");
            setDone(false);
            setSubmitting(false);
        }
    }, [open]);

    const submit = async (e) => {
        e?.preventDefault?.();
        setError("");
        if (!name.trim()) return setError("Please enter your name");
        if (!validEmail(email)) return setError("Please enter a valid work email");
        setSubmitting(true);
        try {
            await axios.post(`${API}/leads`, {
                name: name.trim(),
                email: email.trim(),
                intent,
                source: "landing_page",
            });
            setDone(true);
            toast.success("You're on the list", {
                description: "We'll be in touch shortly at " + email.trim(),
            });
        } catch (err) {
            setError(
                err?.response?.data?.detail
                    ? "We couldn't save your details. Please try again."
                    : "Network error. Please try again."
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                data-testid="lead-modal"
                className="sm:max-w-md p-0 overflow-hidden border-emerald-500/20 bg-white"
            >
                <div className="relative">
                    <div
                        aria-hidden
                        className="absolute inset-0 bg-gradient-to-br from-[#04110D] via-[#063a2c] to-[#04110D]"
                    />
                    <div className="relative px-6 pt-7 pb-7">
                        <span className="inline-flex items-center gap-2 rounded-full border border-emerald-400/30 bg-emerald-500/10 px-2.5 py-1 text-[10px] tracking-[0.25em] uppercase font-bold text-emerald-200">
                            {label || copy.title}
                        </span>
                        <DialogHeader className="mt-3 text-left">
                            <DialogTitle className="font-heading text-2xl font-extrabold text-white">
                                {copy.title}
                            </DialogTitle>
                            <DialogDescription className="text-zinc-300 mt-1">
                                {copy.desc}
                            </DialogDescription>
                        </DialogHeader>
                    </div>
                </div>

                <div className="px-6 py-6">
                    {done ? (
                        <div data-testid="lead-modal-success" className="text-center py-6">
                            <div className="mx-auto h-12 w-12 rounded-full bg-emerald-100 flex items-center justify-center">
                                <CheckCircle2 className="h-6 w-6 text-emerald-700" />
                            </div>
                            <h3 className="mt-4 font-heading text-xl font-bold text-zinc-950">
                                You're on the list
                            </h3>
                            <p className="mt-2 text-sm text-zinc-600">
                                We'll reach out to <span className="font-mono">{email}</span> shortly.
                            </p>
                            <button
                                type="button"
                                data-testid="lead-modal-close-btn"
                                onClick={() => onOpenChange(false)}
                                className="mt-6 inline-flex h-11 items-center justify-center rounded-full bg-zinc-950 px-6 text-sm font-bold text-white hover:bg-emerald-700 transition-colors"
                            >
                                Done
                            </button>
                        </div>
                    ) : (
                        <form onSubmit={submit} noValidate className="space-y-4">
                            <label className="block">
                                <span className="text-xs font-bold tracking-widest uppercase text-zinc-500">
                                    Full name
                                </span>
                                <input
                                    type="text"
                                    autoFocus
                                    required
                                    data-testid="lead-modal-name-input"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder="Ananya Mehta"
                                    className="mt-1.5 h-12 w-full rounded-xl border border-zinc-200 px-4 text-base text-zinc-950 focus:border-emerald-500 focus:outline-none transition-colors"
                                />
                            </label>
                            <label className="block">
                                <span className="text-xs font-bold tracking-widest uppercase text-zinc-500">
                                    Work email
                                </span>
                                <div className="mt-1.5 relative">
                                    <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
                                    <input
                                        type="email"
                                        required
                                        data-testid="lead-modal-email-input"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder="ananya@company.com"
                                        className="h-12 w-full rounded-xl border border-zinc-200 pl-10 pr-4 text-base text-zinc-950 focus:border-emerald-500 focus:outline-none transition-colors"
                                    />
                                </div>
                            </label>

                            {error && (
                                <div
                                    data-testid="lead-modal-error"
                                    className="text-sm text-red-600"
                                >
                                    {error}
                                </div>
                            )}

                            <button
                                type="submit"
                                disabled={submitting}
                                data-testid="lead-modal-submit-btn"
                                className="mt-2 inline-flex h-12 w-full items-center justify-center gap-2 rounded-full bg-zinc-950 px-6 text-base font-bold text-white hover:bg-emerald-700 transition-colors disabled:opacity-60"
                            >
                                {submitting ? (
                                    <>
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                        Submitting…
                                    </>
                                ) : (
                                    <>Get on the list</>
                                )}
                            </button>
                            <p className="text-[11px] text-zinc-500 text-center">
                                By submitting you agree to receive updates from TruoPay. No spam.
                            </p>
                        </form>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}
